import styled from "styled-components";

import Remove from "@assets/img/remove-icon.svg";
import VinylPlaceholder from "@assets/img/vinyl-placeholder.svg";
import { IVinyl } from "@src/types/vinyl";
import Tooltip from "@src/components/Tooltip";
import { Tooltip as ReactTooltip } from "react-tooltip";

export const VinylCard = ({
  vinyl,
  onSelect,
  added,
  hideButtons,
}: {
  vinyl: Partial<IVinyl>;
  onSelect: (vinyl: Partial<IVinyl> | null) => void;
  added?: boolean;
  hideButtons?: boolean;
}) => {
  return (
    <VinylCardContainer $active={added}>
      <VinylCardContent>
        <VinylCardImage
          src={
            vinyl?.x_studio_discogs_image_link &&
            vinyl.x_studio_discogs_image_link !== ""
              ? vinyl.x_studio_discogs_image_link
              : VinylPlaceholder
          }
        />
        <VinylTitle className="">
          <Tooltip content={vinyl.name}>{vinyl.name}</Tooltip>
        </VinylTitle>
        <VinylCardDescription data-tooltip-id={`tooltip-content-${vinyl.id}`}>
          {vinyl.description_demo}
        </VinylCardDescription>
        <ReactTooltip
          style={{
            userSelect: "none",
            zIndex: 9999,
            maxWidth: "150px",
          }}
          float
          delayShow={200}
          id={`tooltip-content-${vinyl.id}`}
        >
          {vinyl.description_demo}
        </ReactTooltip>
        {!hideButtons && (
          <AddButton
            disabled={added}
            added={added}
            onClick={() => (added ? onSelect(null) : onSelect(vinyl))}
          >
            {added ? "Added" : "+ Add"}
          </AddButton>
        )}
      </VinylCardContent>
    </VinylCardContainer>
  );
};

const VinylTitleAndDescription = ({
  vinyl,
  tooltipId,
}: {
  vinyl: Partial<IVinyl> | null;
  tooltipId: string;
}) => {
  return (
    <ReactTooltip float delayShow={1500} id={tooltipId}>
      <VinylCard
        onSelect={() => {}}
        vinyl={vinyl as Partial<IVinyl>}
        key={vinyl?.name}
        added={false}
        hideButtons
      />
    </ReactTooltip>
  );
};
export const VinylCardSelected = ({
  vinyl,
  onRemoveVinyl,
  dashed,
}: {
  vinyl: Partial<IVinyl> | null;
  onRemoveVinyl?: () => void;
  /** Dotted outline marking an "extra" pick that sits outside the top 5. */
  dashed?: boolean;
}) => {
  const hasCover =
    !!vinyl?.x_studio_discogs_image_link &&
    vinyl.x_studio_discogs_image_link !== "";
  return (
    <VinylCardSelectedContainer
      data-testid="selected-vinyl-slot"
      data-tooltip-id={`tooltip-content-selected-${vinyl?.id}`}
    >
      <VinylCardImage
        $dashed={dashed}
        style={{ width: 100, height: 100 }}
        src={hasCover ? vinyl?.x_studio_discogs_image_link : VinylPlaceholder}
      />
      {/* A pick whose cover couldn't be resolved (no stored imageUrl and gone
          from the catalogue) would otherwise look exactly like an empty slot. */}
      {vinyl?.id && !hasCover && (
        <MissingCoverName>{vinyl?.name}</MissingCoverName>
      )}
      <VinylTitleAndDescription
        vinyl={vinyl}
        tooltipId={`tooltip-content-selected-${vinyl?.id}`}
        key={`tooltip-content-selected-${vinyl?.id}`}
      />
      {vinyl?.id && !!onRemoveVinyl && (
        <RemoveButton onClick={() => (vinyl?.id ? onRemoveVinyl?.() : null)}>
          <img style={{ objectFit: "cover" }} src={Remove} />
        </RemoveButton>
      )}
    </VinylCardSelectedContainer>
  );
};

const VinylCardContainer = styled.div<{ $active?: boolean }>`
  max-width: 213px;
  width: 213px;
  max-height: 344px;
  height: 344px;
  border: 1px solid
    ${(props) =>
      props.$active ? props.theme.colors.primary : props.theme.colors.gray300};
  display: flex;
  flex-direction: column;
`;
const VinylCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  margin-left: 30px;
  margin-right: 30px;
  margin-top: 20px;
  min-height: 312px;
`;
const VinylCardImage = styled.img<{ $dashed?: boolean }>`
  width: 152px;
  height: 149px;
  border-radius: 5px;
  box-shadow: ${(props) =>
    props.$dashed ? "none" : "0 0 0 2px rgba(0, 0, 0, 0.3) inset"};
  border: ${(props) =>
    props.$dashed
      ? `1px dashed ${props.theme.colors.gray}`
      : `1px solid ${props.theme.colors.gray}4D`};
`;

const VinylTitle = styled.span`
  color: ${(props) => props.theme.colors.primary};
  font-weight: 700;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 500;
  align-self: flex-start;
  min-height: 20px;
  max-width: 100%;
  cursor: default;
`;
const VinylCardDescription = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.gray200};
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4; /* Limit to 4 lines */
  -webkit-box-orient: vertical;
  font-weight: 400;
  color: ${(props) => props.theme.colors.gray};
  min-height: 57px;
  max-width: 100%;
`;
const AddButton = styled.button<{ added?: boolean }>`
  background: transparent;
  background-color: ${(props) =>
    props.added ? props.theme.colors.primary : "transparent"};
  border: 1px solid ${(props) => props.theme.colors.primary};
  color: ${(props) =>
    props.added ? props.theme.colors.black : props.theme.colors.primary};
  width: 54px;
  height: 22px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  transition: background-color 0.3s ease;
  padding: 0 5px;
  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
  }
  &:disabled {
    cursor: not-allowed;
  }
  align-self: flex-start;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Exactly the footprint of the loading skeleton's square, so the header
 * doesn't grow (and get clipped) once the selection has loaded.
 */
const VinylCardSelectedContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 100px;
  height: 100px;
`;

const MissingCoverName = styled.span`
  position: absolute;
  inset: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.7);
  color: ${(props) => props.theme.colors.primary};
  font-size: 11px;
  line-height: 1.3;
  pointer-events: none;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.black};
  cursor: pointer;
  height: 22px;
  width: 22px;
  z-index: 1;
`;
